import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomRichTextEditor from '../../components/CustomRichTextEditor';
import ForceLogoutDialog from '../../components/ForceLogoutDialog';
import { Bell, Menu, Volume2, FileText, Plus, BookOpen, MessageSquare, X, ArrowUpCircle, GripHorizontal } from 'lucide-react';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { Player } from '@lottiefiles/react-lottie-player';
import Split from 'react-split';
import { toast, Toaster } from 'react-hot-toast';
import { TranslatorDialog } from '../../translator';
import { API_BASE } from '../../config/api';
import useExamHeartbeat from '../../utils/useExamHeartbeat';
import useTabSwitchCount from '../../utils/useTabSwitchCount';
import useLivePresence from '../../utils/useLivePresence';


// Count words in the rich-text answer (HTML string): strip tags, collapse
// whitespace, split. Used for the live "số chữ" counter under the Writing editor.
const countWords = (html) => {
  const text = String(html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&[a-z]+;/gi, ' ')
    .trim();
  if (!text) return 0;
  return text.split(/\s+/).filter(Boolean).length;
};

const processInstructions = (instructions) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(instructions, 'text/html');

  doc.querySelectorAll('img').forEach(img => {
    if (img.src.startsWith('/')) {
      img.src = `${API_BASE}${img.src}`;
    }
  });

  return doc.body.innerHTML;
};

// Render AI answers with light markdown: **bold**, "- " bullets, paragraphs.
const formatAiAnswer = (t) => {
  const esc = (s) => (s || '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
  let h = esc(t).replace(/\*\*(.+?)\*\*/g, '<b>$1</b>');
  const lines = h.split('\n');
  const out = []; let inList = false;
  for (const ln of lines) {
    const m = ln.match(/^\s*[-•]\s+(.*)/);
    if (m) { if (!inList) { out.push('<ul class="list-disc pl-5 space-y-0.5 my-1">'); inList = true; } out.push(`<li>${m[1]}</li>`); }
    else { if (inList) { out.push('</ul>'); inList = false; } if (ln.trim()) out.push(`<p class="mb-1">${ln}</p>`); }
  }
  if (inList) out.push('</ul>');
  return out.join('');
};

const WritingLayout = () => {
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const location = useLocation();
  // location.state is lost on a full page reload — persist the task identity so
  // reloading (or navigating back) into the writing room restores it instead of
  // hanging on a blank loading screen forever.
  const navState = useMemo(() => {
    const s = location.state;
    if (s && s.taskId) {
      try { sessionStorage.setItem('writing_room_nav', JSON.stringify({ taskId: s.taskId, testId: s.testId, isForecast: s.isForecast, mode: s.mode, partNumber: s.partNumber })); } catch (e) { /* ignore */ }
      return s;
    }
    try {
      const saved = sessionStorage.getItem('writing_room_nav');
      if (saved) return JSON.parse(saved);
    } catch (e) { /* ignore */ }
    return s || {};
  }, [location.state]);
  const { taskId, testId, isForecast } = navState;
  // Live "người đang làm bài": full test keys by testId, forecast by "<exam>p<part>".
  const liveTakers = useLivePresence(
    testId ? (isForecast && navState.partNumber ? `${testId}p${navState.partNumber}` : String(testId)) : null,
    !!testId
  );
  // Thi thử (exam): 60' timer + auto-submit. Luyện tập (practice) / forecast:
  // no timer, submit anytime.
  const isExamMode = navState.mode === 'exam' && !isForecast;
  const [studentAnswer, setStudentAnswer] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isBellOpen, setIsBellOpen] = useState(false);
  const [wifiStatus, setWifiStatus] = useState({
    isConnected: true, strength: 'Good', showTooltip: false,
    type: 'wifi', downlink: 0, effectiveType: '4g', rtt: 0, saveData: false,
  });
  const [textSize, setTextSize] = useState('regular');
  const [colorTheme, setColorTheme] = useState('black-on-white');
  const menuRef = useRef(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [currentPartIndex, setCurrentPartIndex] = useState(0);
  const [parts, setParts] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submissionData, setSubmissionData] = useState(null);
  const [showForceLogoutDialog, setShowForceLogoutDialog] = useState(false);
  const [logoutCountdown, setLogoutCountdown] = useState(40);
  const [logoutMessage, setLogoutMessage] = useState('');
  const [sampleOpen, setSampleOpen] = useState(false);
  // Word-click dictionary + "Add to New Words" (writing). Attached to readable
  // panels only (prompt/instructions + sample essay), so it never interferes with
  // the answer editor.
  const [vocabMenu, setVocabMenu] = useState({ visible: false, x: 0, y: 0, text: '' });
  const [dict, setDict] = useState({ open: false, text: '', pos: { x: 0, y: 0 } });
  const vocabMenuRef = useRef(null);
  // Ask AI (available throughout the test)
  const [askOpen, setAskOpen] = useState(false);
  const [askChat, setAskChat] = useState([]);
  const [askInput, setAskInput] = useState('');
  const [askLoading, setAskLoading] = useState(false);
  const [askContext, setAskContext] = useState('');   // last highlighted excerpt for AI context
  const [assistTab, setAssistTab] = useState('check'); // check | ask
  const [assistErrors, setAssistErrors] = useState(null);
  const [assistChecking, setAssistChecking] = useState(false);
  const [askPos, setAskPos] = useState({ x: 0, y: 0 });  // draggable modal position
  const askDragRef = useRef(null);
  const startTimeRef = useRef(Date.now());   // for "Thời gian đã làm"
  useEffect(() => {
    if (askOpen) setAskPos((p) => (p.x || p.y) ? p : { x: Math.max(12, window.innerWidth - 440), y: 84 });
  }, [askOpen]);
  const startAskDrag = (e) => {
    askDragRef.current = { dx: e.clientX - askPos.x, dy: e.clientY - askPos.y };
    const move = (ev) => setAskPos({
      x: Math.min(window.innerWidth - 80, Math.max(0, ev.clientX - askDragRef.current.dx)),
      y: Math.min(window.innerHeight - 60, Math.max(0, ev.clientY - askDragRef.current.dy)),
    });
    const up = () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
    document.addEventListener('mousemove', move); document.addEventListener('mouseup', up);
  };
  // Sample Writing window: draggable (header) + resizable (native CSS resize handle).
  const [samplePos, setSamplePos] = useState({ x: 0, y: 0 });
  const [sampleSize, setSampleSize] = useState({ w: 560, h: 480 });
  const sampleDragRef = useRef(null);
  const samplePanelRef = useRef(null);
  useEffect(() => {
    if (sampleOpen) setSamplePos((p) => (p.x || p.y) ? p : { x: Math.max(12, (window.innerWidth - 560) / 2), y: 80 });
  }, [sampleOpen]);
  useEffect(() => {
    if (!sampleOpen || !samplePanelRef.current) return;
    const el = samplePanelRef.current;
    const ro = new ResizeObserver(() => {
      const w = el.offsetWidth, h = el.offsetHeight;
      setSampleSize((s) => (s.w === w && s.h === h) ? s : { w, h });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [sampleOpen]);
  const startSampleDrag = (e) => {
    sampleDragRef.current = { dx: e.clientX - samplePos.x, dy: e.clientY - samplePos.y };
    const move = (ev) => setSamplePos({
      x: Math.min(window.innerWidth - 80, Math.max(0, ev.clientX - sampleDragRef.current.dx)),
      y: Math.min(window.innerHeight - 60, Math.max(0, ev.clientY - sampleDragRef.current.dy)),
    });
    const up = () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
    document.addEventListener('mousemove', move); document.addEventListener('mouseup', up);
  };
  // Sample essay may contain **bold** markers (admin can bold text) → render as <strong>.
  const renderSampleHtml = (s) => {
    const esc = (s || 'No sample available').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return { __html: esc.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>') };
  };
  // Capture whatever is currently selected in the essay/prompt (call before the
  // modal steals focus and clears the DOM selection).
  const captureSelection = () => {
    const t = (window.getSelection()?.toString() || '').trim();
    if (t.length >= 2) setAskContext(t);
  };
  const askAI = async () => {
    const q = askInput.trim();
    if (!q || askLoading) return;   // askLoading guard also blocks duplicate submits
    setAskInput(''); setAskLoading(true);
    setAskChat((c) => [...c, { role: 'user', text: q }]);
    try {
      const res = await fetch(`${API_BASE}/ai/writing/assist/ask`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q, context: askContext || '', task_id: task?.task_id }),
      });
      const data = await res.json();
      setAskChat((c) => [...c, { role: 'ai', text: res.ok ? (data.answer || '') : (data.detail || 'Lỗi') }]);
    } catch (e) {
      setAskChat((c) => [...c, { role: 'ai', text: 'Lỗi khi hỏi AI' }]);
    } finally { setAskLoading(false); }
  };
  // Trợ lý AI: Check Errors on the selected excerpt.
  const runAssistCheck = async () => {
    if (!askContext) return;
    setAssistChecking(true);
    try {
      const res = await fetch(`${API_BASE}/ai/writing/assist/check`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: askContext, part_number: task?.part_number || 2 }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Kiểm tra thất bại'); return; }
      setAssistErrors((data.errors || []).map((e) => ({ ...e, _show: false })));
    } catch (e) { toast.error('Lỗi khi kiểm tra'); } finally { setAssistChecking(false); }
  };
  const applyAssistFix = (idx, useHigh = false) => {
    const er = (assistErrors || [])[idx];
    if (!er) return;
    const repl = useHigh ? (er.high_band || er.suggestion) : er.suggestion;
    if (!studentAnswer.includes(er.error)) { toast.error('Không tìm thấy đoạn trong bài (có thể đã sửa).'); return; }
    setStudentAnswer(studentAnswer.replace(er.error, repl));
    setAssistErrors((es) => es.filter((_, i) => i !== idx));
    toast.success('Đã áp dụng.');
  };

  const handleWordSelect = () => {
    setTimeout(() => {
      const selection = window.getSelection();
      const text = (selection?.toString() || '').trim();
      if (text && text.length < 50 && !text.includes(' ')) {
        const rect = selection.getRangeAt(0).getBoundingClientRect();
        setVocabMenu({ visible: true, x: rect.left + rect.width / 2, y: rect.bottom + window.scrollY + 5, text });
      }
    }, 10);
  };

  const saveWritingVocab = async () => {
    if (!vocabMenu.text) return;
    try {
      const res = await fetch(`${API_BASE}/student/vocabulary`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          word: vocabMenu.text,
          source_type: 'writing',
          source_exam_id: testId ? parseInt(testId, 10) : null,
          source_exam_title: task?.part_number ? `Writing Task ${task.part_number}` : 'Writing Test',
        }),
      });
      if (res.ok) toast.success(`"${vocabMenu.text}" đã được thêm vào từ vựng mới (Vocabulary → New Words)!`);
      else toast.error('Không lưu được từ');
    } catch (e) {
      toast.error('Lỗi khi lưu từ');
    }
    setVocabMenu({ visible: false, x: 0, y: 0, text: '' });
  };

  const openDict = () => {
    setDict({ open: true, text: vocabMenu.text, pos: { x: vocabMenu.x, y: vocabMenu.y } });
    setVocabMenu({ visible: false, x: 0, y: 0, text: '' });
  };

  useEffect(() => {
    const onDocClick = (e) => {
      if (vocabMenuRef.current && !vocabMenuRef.current.contains(e.target)) {
        setVocabMenu((m) => (m.visible ? { ...m, visible: false } : m));
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Count tab switches while taking the test.
  const tabSwitches = useTabSwitchCount(!!taskId);
  // Số chữ trước đây chỉ tính tạm trong JSX để hiện dưới ô soạn thảo. Giáo viên cần thấy
  // con số này trong bảng theo dõi trực tuyến (feedback 28/09), nên nâng lên thành giá trị
  // dùng chung — useMemo vì hàm này chạy lại theo từng phím gõ.
  const wordCount = useMemo(() => countWords(studentAnswer), [studentAnswer]);
  // Live progress heartbeat for the teacher realtime board (no-op unless the
  // student belongs to a center).
  useExamHeartbeat({
    enabled: !!taskId,
    skill: 'writing',
    examId: testId,
    title: task?.part_number ? `Writing Task ${task.part_number}` : 'Writing',
    questionsDone: currentPartIndex,
    totalQuestions: parts?.length || null,
    lastQuestion: currentPartIndex + 1,
    part: task?.part_number || null,
    tabSwitches,
    wordCount,
  });
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Live WiFi/network status (same as Reading/Listening rooms).
  useEffect(() => {
    const getSignalStrength = (downlink) => {
      if (!downlink) return 'Unknown';
      if (downlink >= 10) return 'Excellent';
      if (downlink >= 5) return 'Good';
      if (downlink >= 2) return 'Fair';
      return 'Poor';
    };
    let connection;
    const updateNetworkInfo = () => {
      setWifiStatus((prev) => ({
        ...prev,
        isConnected: true,
        type: connection.type || 'wifi',
        downlink: connection.downlink || 0,
        effectiveType: connection.effectiveType || '4g',
        rtt: connection.rtt || 0,
        saveData: connection.saveData || false,
        strength: getSignalStrength(connection.downlink),
      }));
    };
    const checkWifiStatus = () => {
      if (navigator.onLine) {
        connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        if (connection) {
          updateNetworkInfo();
          connection.addEventListener('change', updateNetworkInfo);
        } else {
          setWifiStatus((prev) => ({ ...prev, isConnected: true, strength: 'Good' }));
        }
      } else {
        setWifiStatus((prev) => ({ ...prev, isConnected: false, strength: 'Disconnected' }));
      }
    };
    checkWifiStatus();
    window.addEventListener('online', checkWifiStatus);
    window.addEventListener('offline', checkWifiStatus);
    return () => {
      window.removeEventListener('online', checkWifiStatus);
      window.removeEventListener('offline', checkWifiStatus);
      if (connection) connection.removeEventListener('change', updateNetworkInfo);
    };
  }, []);

  const isSubmitEnabled = (timeRemaining) => {
    // Luyện tập (full test): nộp bất cứ lúc nào. Thi thử / forecast: nút nộp mở
    // trong 2 phút cuối (giữ nguyên hành vi cũ của forecast).
    if (!isExamMode && !isForecast) return true;
    return timeRemaining <= 120 || timeRemaining <= 0;
  };
  const textSizeClasses = {
    regular: 'text-base',
    large: 'text-lg',
    'extra-large': 'text-xl'
  };
  const colorThemeClasses = {
    'black-on-white': 'bg-white text-black',
    'white-on-black': 'bg-black text-white',
    'yellow-on-black': 'bg-black text-yellow-300'
  };
  useEffect(() => {
    const fetchTestParts = async () => {
      const token = localStorage.getItem('token');
      if (!token || !testId || isForecast) return;

      try {
        const response = await fetch(`${API_BASE}/student/writing/tasks`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          const currentTest = data.find(test => test.test_id === parseInt(testId));
          if (currentTest) {
            setParts(currentTest.parts);
            const currentIndex = currentTest.parts.findIndex(part => part.task_id === parseInt(taskId));
            setCurrentPartIndex(currentIndex);
          }
        }
      } catch (error) {
        console.error('Error fetching test parts:', error);
      }
    };

    fetchTestParts();
  }, [testId, taskId, isForecast]);

  // Remove answers from dependency array
  useEffect(() => {
    const fetchTaskDetails = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/student/writing/tasks/${taskId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setTask(data);
          setTimeLeft(data.duration * 60);
          setCurrentPartIndex(data.part_number - 1);

          // Use saved answer if exists, otherwise use previous answer from server
          const savedAnswer = answers[taskId];
          if (savedAnswer) {
            setStudentAnswer(savedAnswer);
          } else if (data.previous_answer) {
            setStudentAnswer(data.previous_answer.answer_text);
          } else {
            setStudentAnswer('');
          }
        } else if (response.status === 401) {
          navigate('/login');
        }
      } catch (error) {
        console.error('Error fetching task details:', error);
      } finally {
        setLoading(false);
      }
    };

    if (taskId) {
      fetchTaskDetails();
    }
  }, [taskId, navigate]); // Removed answers from dependencies

  useEffect(() => {
    // Timer + auto-submit in Thi thử (exam) mode, and for forecast (unchanged).
    if (timeLeft === null || !(isExamMode || isForecast)) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 0) {
          clearInterval(timer);
          handleSubmit(); // Auto submit when time runs out
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, isExamMode]);

  // Add this new state to track both answers
  const [allAnswers, setAllAnswers] = useState({
    part1_answer: '',
    part2_answer: ''
  });

  // Modify handleEditorChange to update both states
  const handleEditorChange = (content) => {
    setStudentAnswer(content);
    // Update the specific part's answer
    setAllAnswers(prev => ({
      ...prev,
      [task.part_number === 1 ? 'part1_answer' : 'part2_answer']: content
    }));
  };

  // Replace the existing handleSubmit with this new version
  const handleSubmit = async () => {
    const token = localStorage.getItem('token');
    try {
      const currentAnswer = studentAnswer;
      const timeTaken = Math.max(0, Math.round((Date.now() - startTimeRef.current) / 1000));
      setAllAnswers(prev => ({
        ...prev,
        [task.part_number === 1 ? 'part1_answer' : 'part2_answer']: currentAnswer
      }));

      if (isForecast) {
        const response = await fetch(
          `${API_BASE}/student/writing/part/${taskId}/essay`,
          {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              answer_text: currentAnswer,
              time_taken: timeTaken
            })
          }
        );

        if (response.ok) {
          const data = await response.json();
          setSubmissionData(data);
          setNotification({
            show: true,
            message: 'Nộp bài thành công! Đang chuyển tới trang đánh giá...',
            type: 'success'
          });
          setTimeout(() => {
            navigate('/writing_review', { state: { testId, isForecast, partNumber: task?.part_number } });
          }, 1200);
        } else {
          setNotification({
            show: true,
            message: 'Failed to submit your answer. Please try again.',
            type: 'error'
          });
        }
        return;
      }

      const response = await fetch(
        `${API_BASE}/student/writing/test/${testId}/submit`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            part1_answer: task.part_number === 1 ? currentAnswer : allAnswers.part1_answer,
            part2_answer: task.part_number === 2 ? currentAnswer : allAnswers.part2_answer,
            time_taken: timeTaken
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSubmissionData(data);

        setNotification({
          show: true,
          message: 'Nộp bài thành công! Đang chuyển tới trang đánh giá...',
          type: 'success'
        });

        setTimeout(() => {
          navigate('/writing_review', { state: { testId, isForecast } });
        }, 1200);
      } else if (response.status === 409) {
        const errorData = await response.json();
        setLogoutMessage(errorData.detail || 'Tài khoản của bạn đã được đăng nhập từ thiết bị khác.');
        setShowForceLogoutDialog(true);
        let countdown = 40;
        setLogoutCountdown(countdown);
        const timer = setInterval(() => {
          countdown -= 1;
          setLogoutCountdown(countdown);
          if (countdown <= 0) {
            clearInterval(timer);
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            navigate('/login');
          }
        }, 1000);
      } else {
        setNotification({
          show: true,
          message: 'Failed to submit your test. Please try again.',
          type: 'error'
        });
      }
    } catch (error) {
      setNotification({
        show: true,
        message: 'Failed to submit your test. Please try again.',
        type: 'error'
      });
    }
  };

  // Manual submit → confirm dialog first (timer auto-submit calls handleSubmit directly).
  const requestSubmit = () => setSubmitConfirmOpen(true);
  const confirmSubmit = async () => {
    setSubmitting(true);
    try {
      await handleSubmit();
    } finally {
      setSubmitting(false);
      setSubmitConfirmOpen(false);
    }
  };

  // Update the submit button text in the return section
  <button
    onClick={handleSubmit}
    className={`flex items-center gap-2 px-7 py-5 rounded-lg transition-colors ${colorTheme === 'black-on-white' ? 'bg-black text-white hover:bg-gray-800' : 'bg-white text-black hover:bg-gray-200'
      }`}
  >
    <Check className="w-5 h-5" />
    <span>Submit Test</span>
  </button>

  const handlePreviousPart = async () => {
    if (currentPartIndex > 0) {
      // Save current answer before switching
      const previousPart = parts[currentPartIndex - 1];
      setAllAnswers(prev => ({
        ...prev,
        [task.part_number === 1 ? 'part1_answer' : 'part2_answer']: studentAnswer
      }));

      setLoading(true);
      try {
        const response = await fetch(`${API_BASE}/student/writing/tasks/${previousPart.task_id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setTask(data);
          // Remove the setTimeLeft line to maintain current timer
          setStudentAnswer(data.part_number === 1 ? allAnswers.part1_answer : allAnswers.part2_answer || '');
          setCurrentPartIndex(currentPartIndex - 1);
        }
      } catch (error) {
        console.error('Error fetching previous task:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleNextPart = async () => {
    if (currentPartIndex < parts.length - 1) {
      // Save current answer before switching
      const nextPart = parts[currentPartIndex + 1];
      setAllAnswers(prev => ({
        ...prev,
        [task.part_number === 1 ? 'part1_answer' : 'part2_answer']: studentAnswer
      }));

      setLoading(true);
      try {
        const response = await fetch(`${API_BASE}/student/writing/tasks/${nextPart.task_id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setTask(data);
          // Remove the setTimeLeft line to maintain current timer
          setStudentAnswer(data.part_number === 1 ? allAnswers.part1_answer : allAnswers.part2_answer || '');
          setCurrentPartIndex(currentPartIndex + 1);
        }
      } catch (error) {
        console.error('Error fetching next task:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading task details...</div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">Task not found</div>
      </div>
    );
  }

  return (
    <div className={`h-screen flex flex-col ${colorThemeClasses[colorTheme]}`}>
      <header className={`border-b border-zinc-500 px-4 py-4 flex justify-between items-center ${colorThemeClasses[colorTheme]}`}>
        <div className="flex items-center space-x-8">
          <span className="text-red-600 font-bold text-3xl">IELTS</span>
          <div className={`text-sm ${colorThemeClasses[colorTheme]}`}>
            <div><p className="font-bold">Test taker ID</p></div>
            <div>
              {(isExamMode || isForecast) ? `${formatTime(timeLeft)} remaining` : 'Luyện tập · không giới hạn'}
            </div>
            {liveTakers > 0 && (
              <div className="flex items-center gap-1.5 mt-0.5 text-[#0096b1] font-semibold text-xs">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0096b1] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#0096b1]"></span>
                </span>
                {liveTakers} người đang làm bài
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-4 font-medium whitespace-nowrap">
          <button
            onClick={() => setSampleOpen(true)}
            disabled={!task?.sample_essay}
            className={`px-3 py-1.5 rounded-lg inline-flex items-center gap-2 text-white font-semibold shadow-sm ${!task?.sample_essay ? 'bg-[#0096b1]/50 cursor-not-allowed' : 'bg-[#0096b1] hover:bg-[#007a90]'}`}
            title={task?.sample_essay ? 'View Sample Writing' : 'No sample available'}
          >
            <span className="text-sm inline-flex items-center gap-2">Xem gợi ý
              <FileText className="w-5 h-5 text-white" />
            </span>
          </button>
          <button
            onMouseDown={captureSelection}
            onClick={() => setAskOpen(true)}
            className="px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 bg-[#eb7e37] text-white hover:bg-[#d66e2a] font-semibold"
            title="Trợ lý AI: kiểm tra lỗi & hỏi đáp"
          >
            <span className="text-sm inline-flex items-center gap-1.5">
              <MessageSquare className="w-4 h-4" /> Trợ lý AI
            </span>
          </button>
          {/* WiFi/network status (live) */}
          <div
            className="relative"
            onMouseEnter={() => setWifiStatus((prev) => ({ ...prev, showTooltip: true }))}
            onMouseLeave={() => setWifiStatus((prev) => ({ ...prev, showTooltip: false }))}
          >
            <div className="relative flex items-center justify-center w-8 h-8">
              {(!wifiStatus.isConnected || wifiStatus.strength === 'Poor' || wifiStatus.strength === 'Unknown') ? (
                <Player speed={0.01} autoplay loop src="/wifi-bad.json" style={{ width: 30, height: 27 }} />
              ) : (
                <Player speed={0.5} autoplay loop src="/wifi.json" style={{ width: 45, height: 45 }} />
              )}
            </div>
            {wifiStatus.showTooltip && (
              <div className={`absolute top-full right-0 mt-2 p-3 rounded-lg shadow-lg z-50 ${colorTheme === 'black-on-white' ? 'bg-white text-gray-800' : 'bg-gray-800 text-white'}`}>
                <div className="text-sm whitespace-nowrap">
                  <div className="flex items-center justify-between mb-2 gap-6">
                    <span className="font-medium">Network Status</span>
                    <span className={`px-2 py-1 rounded-full text-xs ${wifiStatus.isConnected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {wifiStatus.isConnected ? 'Connected' : 'Disconnected'}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center gap-6"><span className="text-gray-500">Type:</span><span className="font-medium capitalize">{wifiStatus.type}</span></div>
                    <div className="flex justify-between items-center gap-6"><span className="text-gray-500">Speed:</span><span className="font-medium">{wifiStatus.downlink} Mbps</span></div>
                    <div className="flex justify-between items-center gap-6"><span className="text-gray-500">Signal:</span>
                      <span className={`font-medium ${wifiStatus.strength === 'Excellent' ? 'text-green-500' : wifiStatus.strength === 'Good' ? 'text-lime-500' : wifiStatus.strength === 'Fair' ? 'text-yellow-500' : 'text-red-500'}`}>{wifiStatus.strength}</span>
                    </div>
                    <div className="flex justify-between items-center gap-6"><span className="text-gray-500">Latency:</span><span className="font-medium">{wifiStatus.rtt}ms</span></div>
                    {wifiStatus.saveData && <div className="text-xs text-yellow-500 mt-1">Data Saver Mode Active</div>}
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* Bell (announcements) */}
          <Bell className={`w-5 h-5 cursor-pointer ${colorTheme !== 'black-on-white' ? 'text-white' : 'text-gray-600'}`} strokeWidth={2.5} onClick={() => setIsBellOpen(!isBellOpen)} />
          {isBellOpen && (
            <div className="fixed inset-0 z-[10000]">
              <div className={`min-h-screen w-full flex flex-col items-center justify-center ${colorThemeClasses[colorTheme]}`}>
                <div className="w-full max-w-3xl bg-opacity-95 p-12 rounded-2xl relative">
                  <h2 className="text-3xl text-lime-600 font-bold">No message found!</h2>
                  <button onClick={() => setIsBellOpen(false)} className="font-bold text-2xl hover:opacity-70 transition-opacity absolute top-10 right-10 bg-gray-300 rounded-full p-2">✕</button>
                </div>
              </div>
            </div>
          )}
          <div className="relative" ref={menuRef}>
            <Menu
              className={`w-5 h-5 cursor-pointer ${colorTheme !== 'black-on-white' ? 'text-white' : 'text-gray-600'}`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            />
            {isMenuOpen && (
              <div className="fixed inset-0 z-50">
                <div className={`min-h-screen w-full flex flex-col items-center justify-center ${colorThemeClasses[colorTheme]}`}>
                  <div className="w-full max-w-3xl bg-opacity-95 p-12 rounded-2xl">
                    <div className="flex justify-between items-center mb-12">
                      <h2 className="text-3xl font-bold">Settings</h2>
                      <button
                        onClick={() => setIsMenuOpen(false)}
                        className="text-2xl hover:opacity-70 transition-opacity"
                      >
                        ✕
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-16">
                      <div className="space-y-8">
                        <div>
                          <h3 className="font-semibold text-2xl mb-6">Text Size</h3>
                          <div className="p-6 border rounded-xl mb-8 text-center">
                            <p className={`${textSizeClasses[textSize]}`}>
                              Sample Text Preview
                            </p>
                          </div>
                          <div className="space-y-4">
                            <label className="flex items-center p-4 bg-white text-black rounded-lg cursor-pointer hover:ring-2 hover:ring-lime-500 transition-all">
                              <input
                                type="radio"
                                name="textSize"
                                value="regular"
                                checked={textSize === 'regular'}
                                onChange={(e) => setTextSize(e.target.value)}
                                className="w-5 h-5 text-lime-500"
                              />
                              <span className="text-base ml-4">Regular</span>
                            </label>
                            <label className="flex items-center p-4 bg-white text-black rounded-lg cursor-pointer hover:ring-2 hover:ring-lime-500 transition-all">
                              <input
                                type="radio"
                                name="textSize"
                                value="large"
                                checked={textSize === 'large'}
                                onChange={(e) => setTextSize(e.target.value)}
                                className="w-5 h-5 text-lime-500"
                              />
                              <span className="text-lg ml-4">Large</span>
                            </label>
                            <label className="flex items-center p-4 bg-white text-black rounded-lg cursor-pointer hover:ring-2 hover:ring-lime-500 transition-all">
                              <input
                                type="radio"
                                name="textSize"
                                value="extra-large"
                                checked={textSize === 'extra-large'}
                                onChange={(e) => setTextSize(e.target.value)}
                                className="w-5 h-5 text-lime-500"
                              />
                              <span className="text-xl ml-4">Extra Large</span>
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-8">
                        <div>
                          <h3 className="font-semibold text-2xl mb-6">Color Theme</h3>
                          <div className="space-y-4">
                            <label className="flex items-center p-4 bg-white text-black rounded-lg cursor-pointer hover:ring-2 hover:ring-lime-500 transition-all">
                              <input
                                type="radio"
                                name="colorTheme"
                                value="black-on-white"
                                checked={colorTheme === 'black-on-white'}
                                onChange={(e) => setColorTheme(e.target.value)}
                                className="w-5 h-5 text-lime-500"
                              />
                              <span className="text-lg ml-4">Black on White</span>
                            </label>
                            <label className="flex items-center p-4 bg-black text-white rounded-lg cursor-pointer hover:ring-2 hover:ring-lime-500 transition-all">
                              <input
                                type="radio"
                                name="colorTheme"
                                value="white-on-black"
                                checked={colorTheme === 'white-on-black'}
                                onChange={(e) => setColorTheme(e.target.value)}
                                className="w-5 h-5 text-lime-500"
                              />
                              <span className="text-lg ml-4">White on Black</span>
                            </label>
                            <label className="flex items-center p-4 bg-black text-yellow-300 rounded-lg cursor-pointer hover:ring-2 hover:ring-lime-500 transition-all">
                              <input
                                type="radio"
                                name="colorTheme"
                                value="yellow-on-black"
                                checked={colorTheme === 'yellow-on-black'}
                                onChange={(e) => setColorTheme(e.target.value)}
                                className="w-5 h-5 text-lime-500"
                              />
                              <span className="text-lg ml-4">Yellow on Black</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}    </div>
        </div>
      </header>

      <div className={`flex-1 flex flex-col min-h-0 ${colorThemeClasses[colorTheme]}`}>
        <div className={`mx-4 mt-2 p-3 rounded-lg border border-gray-300 shrink-0 ${colorThemeClasses[colorTheme]}`}>
          <h3 className={`font-bold text-lg mb-1 ${textSizeClasses[textSize]}`}>Part {task.part_number}</h3>
          <p className={textSizeClasses[textSize]}>
            You should spend about {task.duration} minutes on this task. Write at least {task.word_limit} words.
          </p>
        </div>

        <Split
          sizes={[50, 50]}
          minSize={300}
          gutterSize={10}
          className="flex-1 flex min-h-0"
        >
          <div className={`overflow-y-auto p-4 ${colorThemeClasses[colorTheme]}`} onMouseUp={handleWordSelect}>
            <div
              className={`leading-relaxed ${textSizeClasses[textSize]}`}
              dangerouslySetInnerHTML={{ __html: processInstructions(task.instructions) }}
            />
          </div>

          <div className={`p-4 flex flex-col min-h-0 overflow-y-auto ${colorThemeClasses[colorTheme]}`} onMouseUp={captureSelection}>
            <CustomRichTextEditor
              value={studentAnswer}
              onChange={handleEditorChange}
              textSize={textSize}
              colorTheme={colorTheme}
              key={`${textSize}-${colorTheme}`}
              className={colorThemeClasses[colorTheme]}
            />
            <div className={`flex justify-between items-center mt-4 gap-3 ${colorThemeClasses[colorTheme]}`}>
              {(() => {
                const wc = wordCount;
                const min = task.word_limit;
                const enough = !min || wc >= min;
                return (
                  <span className={`text-sm font-medium ${enough ? 'text-green-600' : 'text-gray-500'}`}>
                    {wc} chữ{min ? ` / tối thiểu ${min}` : ''}
                  </span>
                );
              })()}
              {!isForecast && (
                <div className="flex gap-2">
                  <button
                    className={`p-3 border rounded-lg shadow-sm hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed ${colorTheme === 'black-on-white' ? 'bg-black text-white' : 'bg-white text-black'
                      }`}
                    onClick={handlePreviousPart}
                    disabled={currentPartIndex === 0}
                  >
                    <ChevronLeft className="w-7 h-7" />
                  </button>
                  <button
                    className={`p-3 border rounded-lg shadow-sm hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed ${colorTheme === 'black-on-white' ? 'bg-black text-white' : 'bg-white text-black'
                      }`}
                    onClick={handleNextPart}
                    disabled={currentPartIndex === parts.length - 1}
                  >
                    <ChevronRight className="w-7 h-7" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </Split>

        <div className={`border-t border-lime-500 py-2 px-6 shrink-0 ${colorThemeClasses[colorTheme]}`}>
          <div className="flex justify-between items-center text-lg font-bold">
            <div>
              Part {task.part_number}
            </div>
            <div className="flex gap-2">
              <button
                onClick={requestSubmit}
                className={`flex items-center gap-2 px-7 py-5 rounded-lg transition-colors ${colorTheme === 'black-on-white' ? 'bg-black text-white hover:bg-gray-800' : 'bg-white text-black hover:bg-gray-200'
                  }`}
              >
                <Check className="w-5 h-5" />
                <span>{isForecast ? 'Submit Answer' : 'Submit Test'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {submitConfirmOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-[#2b5356] mb-2">Xác nhận nộp bài</h3>
            <p className="text-gray-600 text-sm mb-6">
              {isForecast
                ? 'Bạn có chắc chắn muốn nộp bài này? Sau khi nộp, bài sẽ được chuyển sang trang đánh giá.'
                : 'Bạn có chắc chắn muốn nộp bài thi? Hãy kiểm tra lại cả hai phần trước khi nộp.'}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setSubmitConfirmOpen(false)}
                disabled={submitting}
                className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-60"
              >
                Hủy
              </button>
              <button
                onClick={confirmSubmit}
                disabled={submitting}
                className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-[#0096b1] text-white hover:bg-[#007a90] disabled:opacity-60 inline-flex items-center gap-1.5"
              >
                <Check className="w-4 h-4" /> {submitting ? 'Đang nộp...' : 'Nộp bài'}
              </button>
            </div>
          </div>
        </div>
      )}

      {notification.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className={`rounded-xl max-w-md w-full p-6 ${colorThemeClasses[colorTheme]}`}>
            <div className="text-center">
              <div className={`text-2xl mb-4 ${notification.type === 'success' ? 'text-lime-600' : 'text-red-600'
                }`}>
                {notification.type === 'success' ? '✓' : '✕'}
              </div>
              <p className={`text-lg mb-6 ${colorThemeClasses[colorTheme]}`}>{notification.message}</p>
              <button
                onClick={() => {
                  setNotification({ show: false, message: '', type: '' });
                  if (notification.type === 'success' && !submissionData?.other_part) {
                    // Test complete → go to the review + AI grading screen.
                    navigate('/writing_review', { state: { testId, isForecast } });
                  }
                }}
                className={`px-6 py-6 rounded-lg text-white ${notification.type === 'success' ? 'bg-lime-500 hover:bg-lime-600' : 'bg-red-500 hover:bg-red-600'
                  } transition-colors`}
              >
                {notification.type === 'success' ?
                  (submissionData?.other_part ? 'Continue to Next Part' : 'Xem đánh giá bài viết')
                  : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      {sampleOpen && (
        <div
          ref={samplePanelRef}
          className={`fixed z-[70] rounded-2xl shadow-2xl border border-gray-300 flex flex-col ${colorThemeClasses[colorTheme]}`}
          style={{
            left: samplePos.x, top: samplePos.y,
            width: sampleSize.w, height: sampleSize.h,
            minWidth: 320, minHeight: 220, maxWidth: '96vw', maxHeight: '92vh',
            resize: 'both', overflow: 'hidden',
          }}
        >
          <div
            onMouseDown={startSampleDrag}
            className="flex justify-between items-center px-4 py-3 border-b border-gray-200 cursor-move select-none rounded-t-2xl bg-gray-50/90"
          >
            <h2 className="text-lg font-bold inline-flex items-baseline gap-2">Sample Writing
              <span className="text-xs font-normal text-gray-400 inline-flex items-center gap-1"><GripHorizontal className="w-3.5 h-3.5" /> giữ &amp; kéo để di chuyển</span>
            </h2>
            <button onClick={() => setSampleOpen(false)} className="px-3 py-1.5 rounded bg-gray-200 hover:bg-gray-300 text-sm">Close</button>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4 whitespace-pre-wrap text-base leading-relaxed" onMouseUp={handleWordSelect} dangerouslySetInnerHTML={renderSampleHtml(task?.sample_essay)} />
        </div>
      )}

      {/* Trợ lý AI — draggable floating panel with 2 tabs (Check Errors + Ask AI) */}
      {askOpen && (
        <div className="fixed z-[95] w-[92vw] max-w-md bg-white text-gray-800 rounded-2xl shadow-2xl border border-gray-200 flex flex-col max-h-[80vh]"
             style={{ left: askPos.x, top: askPos.y }}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 cursor-move select-none bg-[#eb7e37] text-white rounded-t-2xl" onMouseDown={startAskDrag}>
              <div className="flex items-center gap-2 font-bold"><MessageSquare className="w-4 h-4" /> Trợ lý AI <span className="text-xs font-normal text-white/70">(kéo để di chuyển)</span></div>
              <button onMouseDown={(e) => e.stopPropagation()} onClick={() => setAskOpen(false)} className="p-1 rounded hover:bg-white/20"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex border-b border-gray-200 shrink-0">
              {[['check', 'Check Errors'], ['ask', 'Ask AI']].map(([k, lb]) => (
                <button key={k} onClick={() => setAssistTab(k)} className={`flex-1 py-2 text-sm font-semibold ${assistTab === k ? 'text-[#eb7e37] border-b-2 border-[#eb7e37]' : 'text-gray-500'}`}>{lb}</button>
              ))}
            </div>
            {askContext ? (
              <div className="px-4 py-2 bg-[#0096b1]/5 border-b border-gray-100 text-xs text-gray-600 shrink-0">
                <span className="font-medium">Đoạn đã chọn: </span>
                <span className="italic">"{askContext.slice(0, 140)}{askContext.length > 140 ? '…' : ''}"</span>
                <button onClick={() => { setAskContext(''); setAssistErrors(null); }} className="ml-2 text-gray-400 hover:text-gray-600 underline">bỏ</button>
              </div>
            ) : (
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs text-gray-500 shrink-0">
                Mẹo: bôi đen một đoạn trong bài <b>rồi bấm "Trợ lý AI"</b> để kiểm tra / hỏi về đoạn đó.
              </div>
            )}

            {assistTab === 'check' && (
              <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-[160px]">
                {askContext
                  ? <button onClick={runAssistCheck} disabled={assistChecking} className="w-full py-1.5 rounded-lg bg-[#eb7e37] text-white text-sm font-semibold hover:bg-[#d66e2a] disabled:opacity-60">{assistChecking ? 'Đang kiểm tra...' : 'Kiểm tra đoạn đã chọn'}</button>
                  : <p className="text-sm text-gray-400">Bôi đen 1 đoạn trong bài viết để kiểm tra Grammar / Vocabulary / Context / Naturalness.</p>}
                {assistErrors && assistErrors.length === 0 && <p className="text-sm text-green-600">Đoạn này ổn.</p>}
                {assistErrors && assistErrors.map((er, i) => (
                  <div key={i} className="border border-gray-100 rounded-lg p-2 text-sm bg-white">
                    <span className="inline-block text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 mb-1">{er.type}</span>
                    <div className="text-red-600 line-through break-words">{er.error}</div>
                    <div className="text-green-600 break-words">{er.suggestion}</div>
                    {er.high_band && <div className="text-purple-700 break-words mt-0.5"><span className="text-[10px] font-bold uppercase">High-band: </span>{er.high_band}</div>}
                    {er._show && er.explain && <div className="text-gray-500 mt-1">{er.explain}</div>}
                    <div className="flex gap-1 mt-1 flex-wrap">
                      <button onClick={() => applyAssistFix(i)} className="px-2 py-0.5 rounded bg-green-500 text-white text-xs font-semibold">Apply</button>
                      {er.high_band && <button onClick={() => applyAssistFix(i, true)} className="px-2 py-0.5 rounded bg-purple-600 text-white text-xs font-semibold inline-flex items-center gap-0.5"><ArrowUpCircle className="w-3 h-3" /> Band cao</button>}
                      <button onClick={() => setAssistErrors((es) => es.map((e, j) => j === i ? { ...e, _show: !e._show } : e))} className="px-2 py-0.5 rounded bg-gray-100 text-gray-600 text-xs font-semibold">Explain</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {assistTab === 'ask' && (
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-2 min-h-[160px]">
                  {askChat.length === 0 && <p className="text-sm text-gray-400">Hỏi AI về đề bài, cách viết, từ vựng, cấu trúc...</p>}
                  {askChat.map((m, i) => (
                    m.role === 'ai'
                      ? <div key={i} className="text-sm rounded-lg px-3 py-2 bg-gray-100 mr-6 leading-relaxed" dangerouslySetInnerHTML={{ __html: formatAiAnswer(m.text) }} />
                      : <div key={i} className="text-sm rounded-lg px-3 py-2 bg-[#0096b1]/10 ml-6 whitespace-pre-wrap">{m.text}</div>
                  ))}
                  {askLoading && <div className="text-sm text-gray-400">AI đang trả lời...</div>}
                </div>
                <div className="flex gap-2 p-3 border-t border-gray-100 shrink-0">
                  <input value={askInput} onChange={(e) => setAskInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); askAI(); } }}
                    placeholder="Nhập câu hỏi..." className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm" />
                  <button onClick={askAI} disabled={askLoading} className="px-4 py-2 rounded-lg bg-[#0096b1] text-white text-sm font-semibold disabled:opacity-60">Gửi</button>
                </div>
              </>
            )}
        </div>
      )}

      {/* Word-click menu: Add to New Words + dictionary lookup */}
      {vocabMenu.visible && (
        <div
          ref={vocabMenuRef}
          className="fixed z-[100] bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[190px]"
          style={{ left: vocabMenu.x, top: vocabMenu.y }}
        >
          <button
            onClick={saveWritingVocab}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-[#0096b1]/10 hover:text-[#0096b1] flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" /> Add to New Words
          </button>
          <button
            onClick={openDict}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-[#0096b1]/10 hover:text-[#0096b1] flex items-center gap-2 transition-colors"
          >
            <BookOpen className="w-4 h-4" /> Tra từ điển
          </button>
        </div>
      )}

      <TranslatorDialog
        isOpen={dict.open}
        selectedText={dict.text}
        position={dict.pos}
        onClose={() => setDict({ open: false, text: '', pos: { x: 0, y: 0 } })}
        colorTheme={colorTheme}
      />

      <Toaster position="top-right" />

      <ForceLogoutDialog
        isOpen={showForceLogoutDialog}
        message={logoutMessage}
        secondsRemaining={logoutCountdown}
      />
    </div>
  );
};

export default WritingLayout;
